import { bootstrapApplication } from '@angular/platform-browser';
import { AppComponent } from './app/app.component';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withNavigationErrorHandler, withPreloading, withRouterConfig } from '@angular/router';
import {
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { APP_ROUTES } from './routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { AuthService } from './app/core/auth/auth.service';
import { authAppInitializerFactory } from './app/core/auth/auth-app-initializer.factory';
import { RuntimeConfigService } from './app/services/config/runtime-config.service';
import { fsxInterceptor } from './app/core/interceptors/fsx.interceptor.service';
import { ChunkLoadErrorHandler } from './app/core/chunk-recovery/chunk-load-error.handler';
import { chunkNavigationErrorHandler } from './app/core/chunk-recovery/chunk-navigation-error.handler';
import { NetworkAwarePreloadStrategy } from './app/core/chunk-recovery/network-aware-preload.strategy';
import {MAT_RIPPLE_GLOBAL_OPTIONS, provideNativeDateAdapter} from '@angular/material/core';
import { FsxGlobalRippleOptions } from './app/services/material/fsx-global-ripple-options.service';

bootstrapApplication(AppComponent, {
  providers: [
    provideNativeDateAdapter(),
    provideZoneChangeDetection(),
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useExisting: FsxGlobalRippleOptions,
    },
    provideHttpClient(withInterceptors([fsxInterceptor])),
    provideRouter(
      APP_ROUTES,
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
      // Preload lazy chunks (network-aware) so a later navigation still finds the
      // current build's chunks after the tab has gone stale.
      withPreloading(NetworkAwarePreloadStrategy),
      // Recover from a lazy chunk failing to load during navigation by reloading
      // to the intended URL. See the UAT chunk-load issue.
      withNavigationErrorHandler(chunkNavigationErrorHandler),
    ),
    provideAnimations(),
    provideOAuthClient(),
    // Recover from chunk-load failures thrown outside a navigation (e.g. deferred
    // imports); bounded reload with a manual fallback once exhausted.
    { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
    // Load the Prod Soft Launch runtime config before routing so guards can read
    // the restriction flags synchronously (AB172621). Runs on every path,
    // including the auth-skipped register/documents routes.
    provideAppInitializer(() => inject(RuntimeConfigService).load()),
    provideAppInitializer(() => {
      const initializerFn = authAppInitializerFactory(inject(AuthService));
      return initializerFn();
    }),
  ],
}).catch((err) => console.error(err));
